/**
 * TelegramTester — message parser sandbox
 * Tests the carbon message parsing logic without a live bot connection.
 * Parsing logic is intentionally channel-agnostic (reuses whatsappCarbonService).
 */

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { WhatsAppCarbonService } from "../../services/whatsappCarbonService";
import { Bot, Send, RotateCcw, FlaskConical } from "lucide-react";

interface TestResult {
  message: string;
  parsed: ReturnType<typeof WhatsAppCarbonService.parseMessage>;
  co2: number;
  response: string;
  timestamp: Date;
}

const EXAMPLE_MESSAGES = [
  "Track carbon: drove 25km today",
  "Carbon log: electricity 45 kwh, gas 30 kwh",
  "Footprint: 2 servings meat, spent $50 shopping",
  "Track emissions: flight 500km",
  "Carbon: walked 5km, ate vegan lunch",
  "Track carbon: bus 15km, bike 3km",
  "Hello, how are you?", // should be ignored
];

const TelegramTester: React.FC = () => {
  const [input, setInput] = useState("");
  const [results, setResults] = useState<TestResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const runTest = () => {
    if (!input.trim()) return;
    setIsProcessing(true);

    setTimeout(() => {
      try {
        const parsed = WhatsAppCarbonService.parseMessage(input);
        const co2 = parsed ? WhatsAppCarbonService.calculateCO2(parsed) : 0;
        const response = WhatsAppCarbonService.generateResponseMessage(
          parsed,
          co2 > 0 ? co2 : undefined,
        );

        setResults((prev) => [
          { message: input, parsed, co2, response, timestamp: new Date() },
          ...prev,
        ]);
        setInput("");
      } catch (err) {
        console.error("Tester error:", err);
      } finally {
        setIsProcessing(false);
      }
    }, 400);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-blue-500" />
            Message Parser Sandbox
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Simulate messages as if you sent them to the Telegram bot — no live
            connection needed.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="e.g. Track carbon: drove 20km today"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  runTest();
                }
              }}
              className="flex-1"
            />
            <Button
              onClick={runTest}
              disabled={!input.trim() || isProcessing}
              className="bg-[#229ED9] hover:bg-[#1a8bbf] text-white"
            >
              {isProcessing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Test
                </>
              )}
            </Button>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              Quick examples:
            </p>
            <div className="flex flex-wrap gap-2">
              {EXAMPLE_MESSAGES.map((msg) => (
                <Button
                  key={msg}
                  variant="outline"
                  size="sm"
                  onClick={() => setInput(msg)}
                  className="text-xs"
                >
                  {msg.length > 32 ? msg.slice(0, 32) + "…" : msg}
                </Button>
              ))}
            </div>
          </div>

          {results.length > 0 && (
            <div className="flex justify-between items-center pt-2">
              <span className="text-xs text-muted-foreground">
                {results.length} result{results.length !== 1 ? "s" : ""}
              </span>
              <Button variant="ghost" size="sm" onClick={() => setResults([])}>
                <RotateCcw className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {results.map((r, i) => (
        <Card key={i}>
          <CardContent className="p-4 space-y-3">
            {/* User bubble */}
            <div className="flex gap-3 justify-end">
              <div className="bg-[#229ED9]/10 border border-[#229ED9]/20 rounded-lg p-3 max-w-[85%]">
                <p className="text-sm">{r.message}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {r.timestamp.toLocaleTimeString()}
                </p>
              </div>
            </div>

            {/* Parsed data badges */}
            {r.parsed && (
              <div className="flex flex-wrap gap-2 ml-1">
                {r.parsed.transport && r.parsed.transport.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    🚗 {r.parsed.transport.length} transport
                  </Badge>
                )}
                {r.parsed.home && Object.keys(r.parsed.home).length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    🏠 Home energy
                  </Badge>
                )}
                {r.parsed.food && r.parsed.food.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    🍽️ {r.parsed.food.length} food item
                    {r.parsed.food.length !== 1 ? "s" : ""}
                  </Badge>
                )}
                {r.co2 > 0 && (
                  <Badge className="text-xs bg-green-100 text-green-800 border-green-200">
                    {r.co2.toFixed(2)} kg CO₂
                  </Badge>
                )}
              </div>
            )}

            {/* Bot reply bubble */}
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-[#229ED9] rounded-full flex items-center justify-center flex-shrink-0">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div className="bg-muted rounded-lg p-3 max-w-[85%]">
                <p className="text-sm whitespace-pre-wrap">{r.response}</p>
              </div>
            </div>

            {!r.parsed && (
              <p className="text-xs text-muted-foreground ml-11">
                ℹ️ No carbon keywords detected — bot would ignore this message.
              </p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default TelegramTester;
